import { ViewIds } from '@ardoq/api-types';
import type { RelationshipsViewRenderingData } from '../types';
import type {
  CollapsedGroupIdsSettings,
  RenderingDataSettings,
} from './viewSettingsPartitions';
import type { InteractiveRelationshipsViewLegendState } from 'tabview/relationshipDiagrams/interactiveRelationshipsViewLegendState$';
import { deriveVisualStates } from './visualStatesUtils';
import { getRelationshipsViewLegendContents } from 'tabview/relationshipDiagrams/utils';
import {
  getActiveConditionalFormattingForLegend,
  GraphViewLegendProps,
} from '@ardoq/view-legend';
import { trackInteractiveLegendItemClick } from 'views/viewLegend/tracking';
import { getErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';

export type ToPropsArgs = {
  renderingData: RelationshipsViewRenderingData;
  renderingDataSettings: CollapsedGroupIdsSettings & RenderingDataSettings;
  interactiveRelationshipsViewLegendState: InteractiveRelationshipsViewLegendState;
  isViewpointMode: boolean;
};

export const toProps = ({
  renderingData,
  renderingDataSettings,
  interactiveRelationshipsViewLegendState,
  isViewpointMode,
}: ToPropsArgs) => {
  const { i18nLocale } = renderingData;
  const activeConditionalFormattingForLegend =
    getActiveConditionalFormattingForLegend(
      i18nLocale,
      undefined,
      isViewpointMode,
      renderingData.users
    );

  const additionalRows = getRelationshipsViewLegendContents({
    componentTypes: renderingData.componentTypes,
    referenceTypes: renderingData.referenceModelTypes,
    selectedComponentTypes:
      interactiveRelationshipsViewLegendState.selectedComponentTypes,
    selectedReferenceTypes:
      interactiveRelationshipsViewLegendState.selectedReferenceTypes,
    selectedConditionalFormattingIds:
      interactiveRelationshipsViewLegendState.selectedConditionalFormattingIds,
    hasCollapsedNodes: false,
    hasNonComponentNodes: false,
    hasReferenceParents: false,
    isUserDefinedGrouping: false,
    showComponentSwatches: true,
    activeConditionalFormattingForLegend,
    showComponentShapes: false,
    showReferencesAsLines: true,
    selectableItems: true,
    isNewConditionalFormatting: isViewpointMode,
    i18nLocale,
  });
  const legendProps: GraphViewLegendProps = {
    additionalRows,
    activeDiffMode: null,
    itemClickTrackingFunction: (legendRowLabel: string) => {
      trackInteractiveLegendItemClick({
        viewId: ViewIds.RELATIONSHIPS_3,
        legendRow: legendRowLabel,
      });
    },
  };

  const visualStates = deriveVisualStates(
    interactiveRelationshipsViewLegendState,
    renderingData.rootNode,
    renderingData.links
  );

  return {
    ...renderingData,
    errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
    viewSettings: {
      ...renderingData.viewSettings,
      ...renderingDataSettings,
      highlightDisconnectedComponents: isViewpointMode
        ? false
        : renderingDataSettings.highlightDisconnectedComponents,
    },
    uiPropsAndCallbacks: {
      legendProps,
    },
    visualStates,
  };
};
